// ─── piiDetect ────────────────────────────────────────────────────────────────
//
// Helper compartido de detección de PII para validación de input.
// Consolida los patrones de hasPhi (revisaUtils.js) y sanitizeFreeText
// (pdfSanitizer.js).
//
// API:
//   detectPii(text) → {
//     hit: boolean,
//     matches: Array<{ tipo: string, valor: string }>
//   }
//
// `tipo` es uno de:
//   keyword | rut | email | telefono | nombre_titulo |
//   hospital | institucion | fecha | nombre_completo
//
// Este módulo solo detecta — no sustituye. La sustitución para export PDF
// vive en src/lib/pdfSanitizer.js y mantiene su responsabilidad separada.

function normalizeText(value = '') {
  return String(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

// ─── 1. Keywords clínicas (sobre texto normalizado) ────────────────────────
const KEYWORD_PATTERNS = [
  /\brut\b/,
  /\bficha\b/,
  /\bhistoria clinica\b/,
  /\bnombre completo\b/,
  /\btelefono\b/,
  /\bcorreo\b/,
  /\bmail\b/,
  /\bhabitacion\b/,
  /\bcama\s*\d+/,
  /\bsala\s*\d+/,
  /\bbox\s*\d+/,
  /\bfecha de nacimiento\b/,
];

// ─── 2. RUT chileno (texto original) ───────────────────────────────────────
const RUT_RE = /\b\d{1,2}\.?\d{3}\.?\d{3}-[\dkK]\b/g;

// ─── 3. Email ──────────────────────────────────────────────────────────────
const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

// ─── 4. Teléfono CL ────────────────────────────────────────────────────────
const TELEFONO_RE = /\b(?:\+?56)?\s*9\s?\d{4}\s?\d{4}\b/g;

// ─── 5. Nombre con título ──────────────────────────────────────────────────
// Sin flag /i: la palabra siguiente DEBE empezar con mayúscula (regla de PII).
// "Paciente femenina" no es PII; "Paciente Juan" sí. Por eso los títulos
// llevan alternates explícitos en lugar de /i.
const NOMBRE_TITULO_RE =
  /\b(?:[Pp]aciente|[Ss]r\.?|[Ss]ra\.?|[Dd]on|[Dd]ona|[Dd]oña)\s+[A-ZÁÉÍÓÚÑ][a-záéíóúñ]+(?:\s+[A-ZÁÉÍÓÚÑ][a-záéíóúñ]+){0,2}/gu;

// ─── 6. Blacklist hospitales (copiada literal de pdfSanitizer.js) ──────────
const HOSPITAL_BLACKLIST = [
  'Félix Bulnes',
  'Felix Bulnes',
  'Salvador',
  'San José',
  'San Jose',
  'Sótero del Río',
  'Sotero del Rio',
  'Barros Luco',
  'San Borja',
  'San Juan de Dios',
  'Calvo Mackenna',
  'Roberto del Río',
  'Roberto del Rio',
  'Exequiel González Cortés',
  'Padre Hurtado',
  'Carlos van Buren',
  'Gustavo Fricke',
  'Sotero',
  'Indisa',
  'Las Condes',
  'Alemana',
  'Dávila',
  'Davila',
  'UC Christus',
];

// ─── 7. Patrón institucional: Hospital/Clínica/CESFAM + nombre ─────────────
const INSTITUCION_RE =
  /\b(?:Hospital|Clínica|Clinica|Centro\s+de\s+Salud|CESFAM|Posta)\s+(?:[A-ZÁÉÍÓÚÑ][\wÁÉÍÓÚÑáéíóúñ'-]+(?:\s+(?:de|del|la|las|el|los|San|Santa|Santo))?\s*){1,4}/g;

// ─── 8. Fechas dd/mm/yyyy · dd-mm-yyyy · dd.mm.yyyy ────────────────────────
const FECHA_RE = /\b(?:0?[1-9]|[12]\d|3[01])[/.-](?:0?[1-9]|1[0-2])[/.-](?:19|20)\d{2}\b/g;

// ─── 9. Nombre completo con stop-words (copiada literal de pdfSanitizer) ───
const NOMBRE_COMPLETO_RE = /\b[A-ZÁÉÍÓÚÑ][a-záéíóúñ]{2,}(?:\s+[A-ZÁÉÍÓÚÑ][a-záéíóúñ]{2,}){1,3}\b/g;

const NOMBRE_STOPWORDS = new Set([
  'Diagnóstico', 'Diagnostico', 'Médico', 'Medico', 'Paciente',
  'Hospital', 'Clínica', 'Clinica', 'Centro', 'Salud',
  'Servicio', 'Unidad', 'Sala', 'Cama', 'Box',
  'Antecedentes', 'Mórbidos', 'Morbidos', 'Quirúrgicos', 'Quirurgicos',
  'Alergias', 'Hábitos', 'Habitos', 'Farmacoterapia', 'Evolución', 'Evolucion',
  'Motivo', 'Ingreso', 'Consulta',
  'Frecuencia', 'Cardíaca', 'Cardiaca', 'Respiratoria',
  'Presión', 'Presion', 'Arterial', 'Saturación', 'Saturacion',
  'Temperatura', 'Glasgow',
  'Día', 'Dia', 'Días', 'Dias', 'Hora', 'Horas', 'Mañana', 'Manana', 'Tarde', 'Noche',
  'Lunes', 'Martes', 'Miércoles', 'Miercoles', 'Jueves', 'Viernes', 'Sábado', 'Sabado', 'Domingo',
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
  // Vocabulario clínico capitalizado que disparaba nombre_completo en texto
  // legítimo (auditoría 2026-07-24: "Sonda Foley", "Escala Braden", "Ringer
  // Lactato", "Aquacel Plata" bloqueaban la guarda dura). Insumos, escalas y
  // vías de administración no son nombres de personas.
  'Sonda', 'Foley', 'Escala', 'Braden', 'Norton', 'Downton', 'Morse', 'Ramsay', 'Aldrete', 'Richmond', 'Bristol',
  'Suero', 'Ringer', 'Lactato', 'Fisiológico', 'Fisiologico', 'Glucosado', 'Glucosalino',
  'Aquacel', 'Plata', 'Duoderm', 'Moltopren', 'Tegaderm',
  'Bomba', 'Infusión', 'Infusion', 'Catéter', 'Cateter', 'Vía', 'Via',
  'Endovenosa', 'Endovenoso', 'Venosa', 'Venoso', 'Oral', 'Subcutánea', 'Subcutanea', 'Intramuscular',
  'Control', 'Signos', 'Vitales', 'Examen', 'Físico', 'Fisico',
  'Balance', 'Hídrico', 'Hidrico', 'Curación', 'Curacion', 'Avanzada',
  // Verbos clínicos que abren oración: capitalizados por puntuación (dictado
  // fiel incluido), no por ser nombres — "Recibe Ceftriaxona Endovenosa"
  // marcaba nombre_completo por el verbo inicial.
  'Recibe', 'Presenta', 'Ingresa', 'Refiere', 'Evoluciona', 'Destaca', 'Inicia',
  'Continúa', 'Continua', 'Mantiene', 'Persiste', 'Traslada', 'Instala',
  'Administra', 'Queda', 'Pasa', 'Realiza', 'Tolera',
]);

// ─── 10. Nombres de pila, minúsculas incluidas ─────────────────────────────
// Incidente real del piloto Bupa (2026-08-11): "sebastian undurraga" tipeado
// TODO en minúscula avanzaba hasta el documento, porque todos los patrones de
// nombre exigen mayúscula inicial — y los teclados institucionales van
// configurados "todo con minúscula". Lista curada de nombres de pila
// frecuentes en Chile, normalizados (sin tildes): un token de esta lista es
// casi seguro un nombre de persona. Precisión primero: quedan FUERA los
// ambiguos con vocabulario clínico o cotidiano (ana por los anticuerpos ANA,
// luz, rosa, blanca, clara, cruz, paz, sol, dolores, angel, concepcion,
// mercedes, esperanza, santiago, german). No agregar uno sin pensar en qué
// frase clínica legítima podría aparecer.
const NOMBRES_PILA = new Set([
  // masculinos
  'sebastian', 'matias', 'benjamin', 'vicente', 'joaquin', 'agustin', 'tomas',
  'cristobal', 'maximiliano', 'alonso', 'felipe', 'ignacio', 'diego', 'javier',
  'jose', 'juan', 'pedro', 'pablo', 'carlos', 'jorge', 'luis', 'manuel',
  'francisco', 'fernando', 'rodrigo', 'ricardo', 'roberto', 'eduardo',
  'andres', 'gonzalo', 'mauricio', 'marcelo', 'patricio', 'cristian',
  'christian', 'daniel', 'david', 'gabriel', 'hector', 'hugo', 'oscar',
  'raul', 'ruben', 'sergio', 'victor', 'alejandro', 'alvaro', 'camilo',
  'claudio', 'esteban', 'guillermo', 'leonardo', 'mario', 'miguel', 'nicolas',
  'rafael', 'samuel', 'emilio', 'renato', 'bruno', 'lucas', 'mateo', 'martin',
  'gaspar', 'octavio', 'ernesto', 'enrique', 'alfredo', 'arturo', 'humberto',
  // femeninos
  'valentina', 'catalina', 'francisca', 'fernanda', 'javiera', 'constanza',
  'antonia', 'isidora', 'florencia', 'martina', 'emilia', 'sofia', 'amanda',
  'maite', 'josefa', 'agustina', 'camila', 'daniela', 'carolina', 'alejandra',
  'andrea', 'angela', 'barbara', 'cecilia', 'claudia', 'veronica', 'monica',
  'natalia', 'paula', 'paulina', 'pamela', 'patricia', 'roxana', 'sandra',
  'marcela', 'lorena', 'karen', 'katherine', 'tamara', 'macarena', 'gloria',
  'ines', 'teresa', 'carmen', 'marta', 'maria', 'elena', 'isabel', 'susana',
  'ximena', 'viviana', 'magdalena', 'rocio', 'loreto', 'pilar', 'bernardita',
  'josefina', 'matilde', 'laura', 'julia', 'gabriela', 'victoria', 'silvia',
]);

// Escapa metacaracteres regex de un literal de la blacklist.
function escapeRegex(literal) {
  return literal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function collectAll(text, regex, tipo) {
  const out = [];
  // matchAll requiere flag /g; nuestras regex la traen.
  for (const m of text.matchAll(regex)) {
    out.push({ tipo, valor: m[0] });
  }
  return out;
}

export function detectPii(text) {
  if (text == null || typeof text !== 'string' || !text.trim()) {
    return { hit: false, matches: [] };
  }

  const original = text;
  const normalized = normalizeText(text);
  const matches = [];

  // 1. Keywords clínicas
  for (const re of KEYWORD_PATTERNS) {
    const m = normalized.match(re);
    if (m) matches.push({ tipo: 'keyword', valor: m[0] });
  }

  // 2-4. Patterns puros
  matches.push(...collectAll(original, RUT_RE, 'rut'));
  matches.push(...collectAll(original, EMAIL_RE, 'email'));
  matches.push(...collectAll(original, TELEFONO_RE, 'telefono'));

  // 5. Nombre con título
  matches.push(...collectAll(original, NOMBRE_TITULO_RE, 'nombre_titulo'));

  // 6. Blacklist hospitales (case-insensitive)
  for (const item of HOSPITAL_BLACKLIST) {
    const re = new RegExp(`\\b${escapeRegex(item)}\\b`, 'gi');
    for (const m of original.matchAll(re)) {
      matches.push({ tipo: 'hospital', valor: m[0] });
    }
  }

  // 7. Patrón institucional
  matches.push(...collectAll(original, INSTITUCION_RE, 'institucion'));

  // 8. Fechas dd/mm/yyyy
  matches.push(...collectAll(original, FECHA_RE, 'fecha'));

  // 9. Nombre completo con stop-words. Rechazar matches donde menos de 2
  //    palabras son nombres reales (todas en stop-list son falsos positivos
  //    típicos en texto clínico: "Frecuencia Cardíaca", "Presión Arterial").
  for (const m of original.matchAll(NOMBRE_COMPLETO_RE)) {
    const palabras = m[0].split(/\s+/);
    const nonStop = palabras.filter(p => !NOMBRE_STOPWORDS.has(p));
    if (nonStop.length >= 2) {
      matches.push({ tipo: 'nombre_completo', valor: m[0] });
    }
  }

  // 10. Nombres de pila sobre el texto NORMALIZADO (minúsculas y sin tildes):
  //     independiente de cómo se tipeó. Si al nombre le sigue otra palabra,
  //     viaja el par completo en `valor` para que el aviso muestre el nombre
  //     con su apellido y sea obvio qué corregir.
  {
    const tokens = [...normalized.matchAll(/\b[a-zñ]{3,}\b/g)];
    for (let i = 0; i < tokens.length; i++) {
      if (!NOMBRES_PILA.has(tokens[i][0])) continue;
      const sig = tokens[i + 1];
      matches.push({ tipo: 'nombre_pila', valor: sig ? `${tokens[i][0]} ${sig[0]}` : tokens[i][0] });
    }
  }

  return { hit: matches.length > 0, matches };
}

// ─── Guarda por niveles (auditoría 2026-07-24) ─────────────────────────────
// Tipos de ALTA precisión: cuando aparecen, es casi seguro que hay un dato
// identificable — son los que habilitan la guarda DURA (bloquear el envío a
// IA). `fecha` y `keyword` quedan deliberadamente fuera: una fecha de cultivo
// ("hemocultivos del 20/07/2026") o "cama 4" son texto clínico legítimo y
// bloquear por eso entrenaba al piloto a pelear con la guarda. Esos tipos
// avisan (detectPii completo), no bloquean.
export const TIPOS_PII_DUROS = new Set([
  'rut', 'email', 'telefono', 'nombre_titulo', 'hospital', 'institucion', 'nombre_completo',
  'nombre_pila',
]);

// Solo los matches de alta precisión: la base de la guarda dura.
export function detectPiiDuro(text) {
  const duros = detectPii(text).matches.filter((m) => TIPOS_PII_DUROS.has(m.tipo));
  return { hit: duros.length > 0, matches: duros };
}

// Fragmentos únicos detectados, listos para mostrar en el aviso: la enfermera
// tiene que saber QUÉ gatilló la guarda para poder corregirlo (un "quítalo"
// sin decir qué, en un relato largo, era adivinanza).
export function fragmentosPii(matches, max = 3) {
  return [...new Set((matches || []).map((m) => `"${m.valor}"`))].slice(0, max).join(', ');
}

// Variante orientada a campos: corre detectPii por cada item y devuelve
// la lista de campos con PII (preservando fieldId y fieldLabel para que
// el banner identifique exactamente dónde hay que corregir, y el padre
// pueda resaltar el textarea/input afectado).
//
// fields: Array<{ fieldId: string, fieldLabel: string, text: string }>
//
// Campos con text vacío o sin matches se omiten silenciosamente.
export function detectPiiByField(fields) {
  if (!Array.isArray(fields) || fields.length === 0) {
    return { hit: false, fieldsWithPii: [] };
  }
  const fieldsWithPii = [];
  for (const f of fields) {
    if (!f || typeof f.text !== 'string' || !f.text.trim()) continue;
    const result = detectPii(f.text);
    if (result.hit) {
      fieldsWithPii.push({
        fieldId: f.fieldId,
        fieldLabel: f.fieldLabel,
        matches: result.matches,
      });
    }
  }
  return { hit: fieldsWithPii.length > 0, fieldsWithPii };
}

// ─── Telemetría de la guarda (SPEC 2.10, Fase A del piloto) ────────────────
// Categorías de PII detectadas en un texto, para contarlas SIN el valor: a la
// telemetría viaja "rut,telefono", jamás el RUT ni el teléfono ni la nota.
export function tiposPii(text) {
  return [...new Set(detectPii(text).matches.map((m) => m.tipo))].sort();
}
